'use client';

import classnames from 'classnames';
import React, { useEffect, useMemo, useRef } from 'react';

import {
    CalendarBarDateItem,
    CalendarBarDateItemProps
} from '@/app/[locale]/(default-layout)/program/_components/CalendarBarDateItem';
import {
    CalendarBarItemProps,
    CalendarBarItemStyle,
    CalendarItemStyleCSSProperties
} from '@/app/[locale]/(default-layout)/program/_components/CalendarBarItem';

import { ButtonWithIconStyle } from '@/components/buttons/ButtonWithIcon';

import { styleFaded } from '@cineamo/ui/src/helpers/style-helper';

import useScrollState from '@cineamo/ui/src/hooks/useScrollState';

import BackAndForthButtonOverlay from '@cineamo/ui/src/button/BackAndForthButtonOverlay';

import MobileOrDesktopRendererSSR from '@cineamo/composables/src/MobileOrDesktopRendererSSR';

import { getDates } from '@/utils/date-helper';

export type TrailingItemVisibility =
    | {
          mobile: Visibility;
          desktop: Visibility;
      }
    | Visibility;

type Visibility = false | 'scroll' | 'sticky';

export type CalendarBarProps<T extends CalendarBarItemProps | CalendarBarDateItemProps> = {
    className?: string;
    selectedDate: string;
    setSelectedDate: (date: string, index: number) => void;
    visibleDaysInFuture?: number;
    showTrailingItem?: TrailingItemVisibility;
    showLeadingItem?: TrailingItemVisibility;
    trailingItem?: React.JSX.Element;
    leadingItem?: React.JSX.Element;
    dateItemWidth?: 'w-80';
    calendarItemStyle?: CalendarBarItemStyle | CalendarItemStyleCSSProperties;
    customCalendarBarItem?: (index: number, props: T) => React.JSX.Element;
    disableScrollIntoView?: boolean;
    /**
     * Extra date box appended after the regular `visibleDaysInFuture` items and before any
     * scrolling trailing item — used for a date picked outside the visible range (see
     * cineamo.com's ProgramCalendar). It scrolls with the date list, unlike the (sticky) date picker.
     */
    extraDateItem?: React.JSX.Element;
};

// Type of T is not really correct!
function CalendarBar<T extends CalendarBarItemProps | CalendarBarDateItemProps>(
    props: CalendarBarProps<T>
): React.JSX.Element {
    const {
        className,
        selectedDate,
        setSelectedDate,
        trailingItem,
        leadingItem,
        dateItemWidth,
        calendarItemStyle,
        customCalendarBarItem,
        disableScrollIntoView,
        extraDateItem
    } = props;

    const visibleDaysInFuture = props.visibleDaysInFuture ? props.visibleDaysInFuture : 14;

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (typeof window === 'undefined' || disableScrollIntoView === true) {
            return;
        }

        const scrollToTarget = () => {
            const scrollTarget =
                // go to selected day if it has showings
                window.document.querySelector(
                    '.__selected__calendar__bar__date.__scroll__target__calendar__bar__date'
                ) ??
                // otherwise go to nearest day with showings
                window.document.getElementsByClassName('__scroll__target__calendar__bar__date')?.[0] ??
                // or to the selected day if there are no showings for any day
                window.document.getElementsByClassName('__selected__calendar__bar__date')?.[0];

            scrollTarget?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            });
        };

        // setTimeout fixes the bug where the scroll was interrupted by rendering and never finished successfully
        const initialScrollTimeout = window.setTimeout(scrollToTarget, 0);

        // The bar's viewport can shrink after the initial scroll, e.g. when the sticky "soon" item
        // widens once its client-side fetched showings render, clipping the scroll target again.
        // Keep re-centering the target on such layout shifts until the user interacts.
        const resizeObserver = new ResizeObserver(scrollToTarget);
        if (scrollContainerRef.current) {
            resizeObserver.observe(scrollContainerRef.current);
        }
        const stopFollowingTarget = () => resizeObserver.disconnect();
        window.addEventListener('wheel', stopFollowingTarget, { passive: true, once: true });
        window.addEventListener('touchstart', stopFollowingTarget, { passive: true, once: true });
        window.addEventListener('pointerdown', stopFollowingTarget, { once: true });

        return () => {
            window.clearTimeout(initialScrollTimeout);
            stopFollowingTarget();
            window.removeEventListener('wheel', stopFollowingTarget);
            window.removeEventListener('touchstart', stopFollowingTarget);
            window.removeEventListener('pointerdown', stopFollowingTarget);
        };
    }, [selectedDate, disableScrollIntoView]);
    const { styleFadedState } = useScrollState({
        scrollContainerRef: scrollContainerRef,
        initialState: {
            startReached: true,
            endReached: false
        }
    });

    const trailingItemVisibility: {
        mobile: Visibility;
        desktop: Visibility;
    } = useMemo(() => {
        if (!props.showTrailingItem) {
            return {
                mobile: false,
                desktop: false
            };
        }
        if (typeof props.showTrailingItem === 'string') {
            return {
                mobile: props.showTrailingItem,
                desktop: props.showTrailingItem
            };
        }
        return props.showTrailingItem;
    }, [props.showTrailingItem]);

    const leadingItemVisibility: {
        mobile: Visibility;
        desktop: Visibility;
    } = useMemo(() => {
        if (!props.showLeadingItem) {
            return {
                mobile: false,
                desktop: false
            };
        }
        if (typeof props.showLeadingItem === 'string') {
            return {
                mobile: props.showLeadingItem,
                desktop: props.showLeadingItem
            };
        }
        return props.showLeadingItem;
    }, [props.showLeadingItem]);

    return (
        <div className="relative w-full flex flex-row">
            <div className="relative w-full flex flex-row space-x-8">
                <div
                    className={classnames(
                        leadingItemVisibility.desktop === 'sticky' && 'lg:sticky',
                        leadingItemVisibility.desktop === false && 'lg:hidden',
                        leadingItemVisibility.mobile === 'sticky' && 'sticky',
                        leadingItemVisibility.mobile === false && 'hidden'
                    )}>
                    {leadingItem}
                </div>
                <div
                    id={'__calendar_bar__'}
                    ref={scrollContainerRef}
                    style={styleFaded(styleFadedState, 10)}
                    className={classnames(
                        className,
                        'group w-full h-auto flex flex-row overflow-x-auto no-scrollbar space-x-8'
                    )}>
                    <MobileOrDesktopRendererSSR
                        desktopContent={leadingItemVisibility.desktop === 'scroll' ? leadingItem : null}
                        mobileContent={leadingItemVisibility.mobile === 'scroll' ? leadingItem : null}
                    />
                    {getDates(visibleDaysInFuture).map((date, index) =>
                        customCalendarBarItem ? (
                            customCalendarBarItem(index, {
                                date: date,
                                selectedDate: selectedDate,
                                setSelectedDate: (date) => setSelectedDate(date, index),
                                dateItemWidth: dateItemWidth,
                                calendarItemStyle: calendarItemStyle
                            } as T)
                        ) : (
                            <CalendarBarDateItem
                                key={index}
                                date={date}
                                selectedDate={selectedDate}
                                setSelectedDate={(date) => setSelectedDate(date, index)}
                                dateItemWidth={dateItemWidth}
                                calendarItemStyle={calendarItemStyle}
                            />
                        )
                    )}
                    {extraDateItem}
                    <MobileOrDesktopRendererSSR
                        desktopContent={trailingItemVisibility.desktop === 'scroll' ? trailingItem : null}
                        mobileContent={trailingItemVisibility.mobile === 'scroll' ? trailingItem : null}
                    />
                </div>

                <MobileOrDesktopRendererSSR
                    desktopContent={trailingItemVisibility.desktop === 'sticky' ? trailingItem : null}
                    mobileContent={trailingItemVisibility.mobile === 'sticky' ? trailingItem : null}
                />
            </div>
            <BackAndForthButtonOverlay
                scrollContainerRef={scrollContainerRef}
                buttonWithIconStyle={ButtonWithIconStyle.transparent}
                renderOutside
            />
        </div>
    );
}

export default CalendarBar;
